import { Flex, HStack, Heading, SkeletonText, Box } from "@chakra-ui/react";
import CDS, { ActionTypeTag } from "design-system";
import { ActionType, ActionV2, PrefixId } from "features/Resources/Shared/models";
import { useForceSendByPrefixId, useActionDetailQueryByPrefixId } from "features/Resources/Shared/hooks";
import { fromActionPrefixId, isProcessed } from "features/Resources/Shared/helpers";
import { actionPathByPrefixId } from "helpers/url/pages/v2/actionPaths";
import ActionSummaryStatusTag from "components/Tag/ActionSummaryStatusTag";
import NewHireStackedLabel from "features/NewHireJourney/Components/NewHireStackedLabel";
import ButtonWithConfirm from "components/Button/ButtonWithConfirm";
import { SecondaryButton } from "components/Button";
import { confirmSendNowArgs } from "features/Resources/Shared/helpers/confirmationArgs";
import { Email } from "features/Resources/Email/models";
import { Task } from "features/Resources/Task/models";
import { Meeting } from "features/Resources/Meeting/models";
import { Message } from "features/Resources/Message/models";
import SideDrawer from "components/SideDrawer";
import EmailDrawerForm from "./EmailDrawerForm";
import MessageDrawerForm from "./MessageDrawerForm";
import TaskDrawerForm from "./TaskDrawerForm";
import MeetingDrawerForm from "./MeetingDrawerForm";
import CheckpointDrawerForm from "./CheckpointDrawerForm";

function ErrorText() {
  return <CDS.Text14>Error loading this. Try again.</CDS.Text14>;
}

interface ActionFormByPrefixIdProps {
  prefixId: PrefixId | undefined | "";
  action: ActionV2 | undefined;
}

function ActionFormByPrefixId({ prefixId, action }: ActionFormByPrefixIdProps) {
  const { resourceType } = fromActionPrefixId(prefixId);
  if (!action) {
    return <CDS.Text14>Loading...</CDS.Text14>;
  }
  switch (resourceType) {
    case "email":
      return <EmailDrawerForm email={action as Email} />;
    case "task":
      return <TaskDrawerForm task={action as Task} />;
    case "message":
      return <MessageDrawerForm message={action as Message} />;
    case "meeting":
      return <MeetingDrawerForm meeting={action as Meeting} />;
    case "checkpoint":
      return <CheckpointDrawerForm checkpoint={action} />;
    case undefined:
    default:
      return <ErrorText />;
  }
}

interface ActionButtonByPrefixIdProps {
  prefixId: PrefixId | undefined | "";
  action: ActionV2 | undefined;
  isLoading?: boolean;
  onClose: () => void;
}

function ActionButtonByPrefixId({ prefixId, action, onClose, isLoading = false }: ActionButtonByPrefixIdProps) {
  const { resourceType } = fromActionPrefixId(prefixId);
  const { mutate: sendNow } = useForceSendByPrefixId(prefixId as PrefixId);
  if (!action || isLoading) {
    return <CDS.Text14>Loading...</CDS.Text14>;
  }
  if (resourceType === "task" && (action as Task).taskType === "custom") return null;
  if (resourceType === "meeting" || resourceType === "checkpoint") return null;
  if (action && !isProcessed(action) && resourceType) {
    return (
      <ButtonWithConfirm
        intent="confirmation"
        isDisabled={!action}
        {...confirmSendNowArgs(resourceType)}
        handleConfirm={() =>
          sendNow(undefined, {
            onSuccess: () => {
              onClose();
            },
          })
        }
      >
        <SecondaryButton height="7">{resourceType === "task" ? "Start Now" : "Send Now"}</SecondaryButton>
      </ButtonWithConfirm>
    );
  }
}

interface ActionSideDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  prefixId: PrefixId | undefined | "";
}

function sideDrawerHeader({
  item: action,
  isLoading,
  onClose,
}: {
  item?: ActionV2 | null;
  isLoading?: boolean;
  onClose: () => void;
}) {
  if (isLoading) {
    return <SkeletonText noOfLines={1} />;
  }
  if (!action) return null;

  const { resourceType } = fromActionPrefixId(action.prefixId);

  return (
    <Flex direction="column" align="flex-start" gap="2">
      <HStack w="100%" align="flex-start" justify="space-between" pr="50px">
        <Flex direction="column">
          <HStack align="center" spacing="2">
            <Box flexShrink={0}>
              <ActionTypeTag.IconBadgeWithMeetingType
                actionType={resourceType as ActionType}
                meetingType={resourceType === "meeting" ? (action as Meeting).meetingType : undefined}
              />
            </Box>
            <Heading fontSize="md" fontWeight="700" lineHeight="19.2px">
              {action.name || "Loading"}
            </Heading>
          </HStack>
          <Box
            as="a"
            href={actionPathByPrefixId(action.prefixId)}
            fontSize="12px"
            fontWeight="500"
            color="brand.500"
            _hover={{ textDecoration: "underline" }}
            mt="2px"
            display="inline-block"
          >
            View details
          </Box>
        </Flex>
        <ActionButtonByPrefixId prefixId={action.prefixId} action={action} onClose={onClose} isLoading={isLoading} />
      </HStack>
      <Flex gap="2" align="center">
        <Flex width="fit-content" my="1">
          {action && <ActionSummaryStatusTag action={action} />}
        </Flex>
      </Flex>
      {action.newHireJourneyId && <NewHireStackedLabel journeyId={action.newHireJourneyId} />}
    </Flex>
  );
}

export default function ActionSideDrawer({ isOpen, onClose, prefixId }: ActionSideDrawerProps) {
  const { data: action, isLoading } = useActionDetailQueryByPrefixId(prefixId as PrefixId, { enabled: !!prefixId });

  if (!prefixId) return null;

  return (
    <SideDrawer
      item={action}
      isOpen={isOpen}
      onClose={onClose}
      header={({ item }) => sideDrawerHeader({ item, isLoading, onClose })}
    >
      {() => <ActionFormByPrefixId prefixId={action?.prefixId} action={action ?? undefined} />}
    </SideDrawer>
  );
}
